import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getToken, getUser } from 'ducks/auth/selectors';
import { fetchLuzmoEmbedToken } from 'services/luzmo';

// refresh a little before the token actually expires
const REFRESH_MARGIN_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 60 * 1000;

const useLuzmoAuth = () => {
  const user = useSelector(getUser);
  const sessionToken = useSelector(getToken);
  const [authKey, setAuthKey] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  const uid = user?.ac_id;
  const org = user?.org;

  useEffect(() => {
    let cancelled = false;
    setAuthKey(null);
    setAuthToken(null);
    setError(null);

    const load = async () => {
      if (!sessionToken) {
        setError('Please sign in to access AI Insights.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await fetchLuzmoEmbedToken();

        if (cancelled) return;

        setAuthKey(token.authKey);
        setAuthToken(token.authToken);

        const expiryDelay = new Date(token.expiry).getTime() - Date.now();
        const refreshDelay = Math.max(
          expiryDelay - REFRESH_MARGIN_MS,
          MIN_REFRESH_DELAY_MS
        );
        timeoutRef.current = setTimeout(load, refreshDelay);
      } catch (err) {
        if (!cancelled) {
          setAuthKey(null);
          setAuthToken(null);
          setError(err?.message || 'Unable to load the Luzmo embed token.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [sessionToken, uid, org]);

  return { authKey, authToken, isLoading, error };
};

export default useLuzmoAuth;
